/* istanbul ignore file  */
import CloseIcon from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	IconButton,
	Slide,
	SlideProps,
	Snackbar,
	Typography
} from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useBoolean } from 'usehooks-ts';
import { IS_TEST_MODE } from 'consts';
import { IToast, SetToastFunc } from 'types/toast';
import ToastContext from './ToastContext';

type TransitionProps = Omit<SlideProps, 'up'>;

const TransitionRight = (props: TransitionProps) => (
	<Slide {...props} direction="right" />
);

const ToastProvider: React.FC<PropsWithChildren> = ({ children = null }) => {
	const { value: open, setTrue, setFalse } = useBoolean(false);
	const [toast, setToast] = React.useState<IToast | null>(null);

	const handleToastChanged = React.useCallback<SetToastFunc>(
		(t: IToast) => {
			/* istanbul ignore if:  */
			if (t?.error && !IS_TEST_MODE) {
				console.error(t?.error); // eslint-disable-line
			}
			setToast(t);
			setTrue();
		},
		[setToast, setTrue]
	);

	return (
		<ToastContext.Provider value={handleToastChanged}>
			<Snackbar
				open={open}
				autoHideDuration={5000}
				onClose={(_event: React.SyntheticEvent | Event, reason?: string) => {
					if (reason === 'clickaway') {
						return;
					}
					setFalse();
				}}
				TransitionComponent={TransitionRight}
				TransitionProps={{
					onAnimationEnd: () => setToast(null)
				}}
				transitionDuration={300}
				sx={{
					position: 'absolute',
					mb: 8,
					whiteSpace: 'break-spaces'
				}}
			>
				<Alert data-testid={toast?.id} severity={toast?.severity}>
					<Box display="flex" alignItems="center" height="auto">
						<Typography variant="body2">
							{(toast?.content || '').replace(/^\n|\n$/g, '')}
						</Typography>
						<IconButton
							size="small"
							data-testid="close-toast-button"
							sx={{ ml: 1, height: 24, width: 24 }}
							onClick={setFalse}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
				</Alert>
			</Snackbar>
			{children}
		</ToastContext.Provider>
	);
};

export default ToastProvider;
